// NodeMission.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsPlaybookFocusEmbed } from 'hooks/useDeviceDetect';

// Utilities
import { MISSION_TASK_HANDLED_BY } from 'missions/taskmodal/missionTaskModalUtils';
import { getMissionActivePathTasks } from 'node/missions/mission/utils/missionRunUtils';
import { applyWorkflowStepNodeToRunTask } from 'node/missions/mission/utils/workflowStepNodeUtils';

// CSS
import './NodeMission.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';
import NodeMissionHeader from 'node/missions/mission/header/NodeMissionHeader';
import NodeMissionMeta from 'node/missions/mission/meta/NodeMissionMeta';
import NodeMissionProgress from 'node/missions/mission/progress/NodeMissionProgress';
import NodeMissionTask from 'node/missions/mission/task/NodeMissionTask';
import NodeMissionTaskStep from 'node/missions/mission/task/step/NodeMissionTaskStep';
import PlaybookItemFocus from 'node/missions/mission/focus/PlaybookItemFocus';
import PlaybookItemFocusModal from 'node/missions/mission/focus/PlaybookItemFocusModal';

const ACTIVE_STEP_STATUSES = new Set(['ready', 'inProgress', 'running', 'blocked']);
const NEXT_STEP_SCROLL_OFFSET_PX = 50;

function isActiveMissionStepStatus(status) {
  return ACTIVE_STEP_STATUSES.has(String(status || '').trim());
}

function findScrollParent(element) {
  let parent = element?.parentElement || null;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    const canScroll = (
      overflowY === 'auto'
      || overflowY === 'scroll'
      || overflowY === 'overlay'
    ) && parent.scrollHeight > parent.clientHeight + 1;
    if (canScroll) return parent;
    parent = parent.parentElement;
  }
  return null;
}

function scrollMissionStepIntoView(stepElement, offsetPx = NEXT_STEP_SCROLL_OFFSET_PX) {
  if (!stepElement) return;

  const scroller = findScrollParent(stepElement);
  if (!scroller) {
    stepElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }

  const scrollerRect = scroller.getBoundingClientRect();
  const stepRect = stepElement.getBoundingClientRect();
  const nextTop = scroller.scrollTop + (stepRect.top - scrollerRect.top) - offsetPx;
  scroller.scrollTo({
    behavior: 'smooth',
    top: Math.max(0, nextTop),
  });
}

/**
 * NodeMission
 *
 * Unbound: clickable workflow card (icon / title / description).
 * Bound: live workflow with progress and a flat task list.
 *
 * @param {object} template - Workflow template.
 * @param {object|null} missionRun - Normalized run, if launched.
 * @param {object} missionProgress - Completion progress.
 * @param {string} missionStatus - Current run status.
 * @param {function} onSelectTemplate - Opens launch preflight for unbound cards.
 * @param {object|null} user - Current signed-in user.
 * @param {object} missionActions - Shared run action handlers and UI state.
 * @param {boolean} [compact=false] - Peek density: collapse non-active steps.
 * @param {Map<string, object>} [stepNodesByKey] - Live TASK nodes keyed by nodeKey.
 * @param {object|null} [hostNode] - Workflow host record (e.g. PROJECT).
 * @param {function} [onHostNodeChange] - Optional local host patch after field saves.
 * @param {function} [onOpenWorkflowSettings] - Opens the workflow designer for this template.
 * @param {object[]|null} [playbookSwitcherEntries] - All templates for the title picker.
 * @param {string} [selectedTemplateKey] - Currently selected template key.
 * @param {function} [onSelectPlaybook] - Selects an existing run or starts an unstarted one.
 * @returns {ReactNode} The rendered workflow card or live run.
 */
const NodeMission = React.memo(({
  template,
  missionRun = null,
  missionProgress,
  missionStatus,
  onSelectTemplate,
  user = null,
  missionActions,
  compact = false,
  stepNodesByKey = null,
  hostNode = null,
  onHostNodeChange = null,
  onOpenWorkflowSettings = null,
  playbookSwitcherEntries = null,
  selectedTemplateKey = '',
  onSelectPlaybook = null,
}) => {
  const { theme } = useTheme();
  const isPlaybookFocusEmbed = useIsPlaybookFocusEmbed();
  const hasRun = Boolean(missionRun);
  const savingMissionKey = missionActions?.savingMissionKey || '';
  const handleCancelMission = missionActions?.handleCancelMission;
  const handleConfirmReopenTask = missionActions?.handleConfirmReopenTask;
  const reopenConfirm = missionActions?.reopenConfirm || null;
  const setReopenConfirmOpen = missionActions?.setReopenConfirmOpen;
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [focusOpen, setFocusOpen] = useState(false);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const [manuallyExpandedTaskKeys, setManuallyExpandedTaskKeys] = useState(() => new Set());
  const stepElementsRef = useRef(new Map());
  const previousActiveTaskKeysRef = useRef(null);
  const tasks = useMemo(
    () => getMissionActivePathTasks(missionRun),
    [missionRun]
  );
  const workflowTitle = String(template?.title || '').trim() || 'this playbook';

  const isTaskBodyExpanded = useCallback((task) => {
    if (!compact) return true;
    const taskKey = String(task?.key || '').trim();
    if (!taskKey) return true;
    if (isActiveMissionStepStatus(task?.status)) return true;
    return manuallyExpandedTaskKeys.has(taskKey);
  }, [compact, manuallyExpandedTaskKeys]);

  const handleToggleTaskBodyExpanded = useCallback((taskKey) => {
    const key = String(taskKey || '').trim();
    if (!key) return;
    setManuallyExpandedTaskKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const taskSteps = useMemo(() => {
    let humanStepNumber = 0;

    return tasks.map((task, index) => {
      const isPeopleTask = task?.handledBy !== MISSION_TASK_HANDLED_BY.BOT;
      if (isPeopleTask) {
        humanStepNumber += 1;
      }

      return {
        isLast: index === tasks.length - 1,
        stepNumber: isPeopleTask ? humanStepNumber : null,
        task,
      };
    });
  }, [tasks]);

  const activeTaskKeys = useMemo(
    () => tasks
      .filter((task) => isActiveMissionStepStatus(task?.status))
      .map((task) => String(task?.key || '').trim())
      .filter(Boolean),
    [tasks]
  );

  useEffect(() => {
    const previousKeys = previousActiveTaskKeysRef.current;
    previousActiveTaskKeysRef.current = activeTaskKeys;

    // Skip first paint so opening a run doesn't jump the list.
    if (previousKeys == null) return;

    const newlyActiveKey = activeTaskKeys.find((key) => !previousKeys.includes(key));
    if (!newlyActiveKey) return;

    const stepElement = stepElementsRef.current.get(newlyActiveKey) || null;
    if (!stepElement) return;

    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollMissionStepIntoView(stepElement);
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [activeTaskKeys]);

  const setStepElementRef = useCallback((taskKey, element) => {
    const key = String(taskKey || '').trim();
    if (!key) return;
    if (element) {
      stepElementsRef.current.set(key, element);
      return;
    }
    stepElementsRef.current.delete(key);
  }, []);

  const showEmbeddedFocus = isPlaybookFocusEmbed && !mobileListOpen;

  /**
   * handleOpenPlaybookFocus
   *
   * Desktop: opens the checklist-item wizard modal.
   * Phone / tablet: returns from the full step list to the embedded wizard.
   */
  const handleOpenPlaybookFocus = useCallback(() => {
    if (isPlaybookFocusEmbed) {
      setMobileListOpen(false);
      return;
    }
    setFocusOpen(true);
  }, [isPlaybookFocusEmbed]);

  /**
   * handleShowPlaybookList
   *
   * Mobile escape hatch from the embedded wizard to the step list.
   */
  const handleShowPlaybookList = useCallback(() => {
    setMobileListOpen(true);
  }, []);

  /**
   * handleClosePlaybookFocus
   *
   * Closes the desktop playbook wizard and returns to the list.
   */
  const handleClosePlaybookFocus = useCallback(() => {
    setFocusOpen(false);
  }, []);

  useEffect(() => {
    if (!isPlaybookFocusEmbed) setMobileListOpen(false);
  }, [isPlaybookFocusEmbed]);

  const handleCancelWorkflow = useCallback(() => {
    if (!template?.key || typeof handleCancelMission !== 'function') return;
    setCancelConfirmOpen(true);
  }, [handleCancelMission, template?.key]);

  const handleConfirmCancelWorkflow = useCallback(async () => {
    setCancelConfirmOpen(false);
    if (!template?.key || typeof handleCancelMission !== 'function') return;
    await handleCancelMission(template);
  }, [handleCancelMission, template]);

  if (!hasRun) {
    const iconKey = String(template?.iconKey || '').trim() || 'account_tree';
    const title = String(template?.title || '').trim() || 'Untitled Playbook';
    const description = String(template?.description || '').trim();

    return (
      <button
        className="node-mission-container node-mission-container-unbound"
        type="button"
        onClick={() => onSelectTemplate?.(template)}
      >
        <div
          className="node-mission-unbound-icon"
          style={{ backgroundColor: theme.backgroundMuted }}
        >
          <Icon
            iconKey={iconKey}
            color={theme.foreground}
            width={24}
            height={24}
          />
        </div>

        <div className="node-mission-unbound-copy">
          <div className="node-mission-unbound-title">
            {title}
          </div>
          {description ? (
            <div
              className="node-mission-unbound-description"
              style={{ color: theme.foregroundMuted }}
            >
              {description}
            </div>
          ) : (
            <div
              className="node-mission-unbound-description"
              style={{ color: theme.foregroundMuted }}
            >
              Tap to assign people and start.
            </div>
          )}
        </div>

        <Icon
          iconKey="chevron_right"
          color={theme.foregroundMuted}
          width={20}
          height={20}
        />
      </button>
    );
  }

  return (
    <div className={`node-mission-container${showEmbeddedFocus ? ' node-mission-container-focus' : ''}`}>
      <div className="node-mission-sticky-header">
        <NodeMissionHeader
          template={template}
          missionStatus={missionStatus}
          hasRun={hasRun}
          onOpenPlaybookFocus={showEmbeddedFocus ? null : handleOpenPlaybookFocus}
          onShowPlaybookList={showEmbeddedFocus ? handleShowPlaybookList : null}
          onOpenWorkflowSettings={onOpenWorkflowSettings}
          playbookSwitcherEntries={playbookSwitcherEntries}
          selectedTemplateKey={selectedTemplateKey}
          onSelectPlaybook={onSelectPlaybook}
        />

        {!showEmbeddedFocus ? (
          <>
            <NodeMissionMeta
              hasRun={hasRun}
              missionProgress={missionProgress}
              missionStatus={missionStatus}
              template={template}
              missionRun={missionRun}
            />

            <NodeMissionProgress
              hasRun={hasRun}
              missionProgress={missionProgress}
            />
          </>
        ) : null}
      </div>

      <div className="node-mission-body">
        {showEmbeddedFocus ? (
          <PlaybookItemFocus
            hostNode={hostNode}
            missionActions={missionActions}
            missionProgress={missionProgress}
            missionRun={missionRun}
            stepNodesByKey={stepNodesByKey}
            template={template}
            user={user}
            onHostNodeChange={onHostNodeChange}
          />
        ) : (
          <>
            <div className="node-mission-task-list">
              {taskSteps.map(({ task, stepNumber, isLast }) => (
                <NodeMissionTaskStep
                  key={task.key}
                  ref={(element) => setStepElementRef(task.key, element)}
                  isLast={isLast}
                  stepNumber={stepNumber}
                  task={task}
                >
                  <NodeMissionTask
                    bodyExpanded={isTaskBodyExpanded(task)}
                    compact={compact}
                    hostNode={hostNode}
                    missionRun={missionRun}
                    stepNode={
                      task?.nodeKey && stepNodesByKey instanceof Map
                        ? stepNodesByKey.get(String(task.nodeKey)) || null
                        : null
                    }
                    task={applyWorkflowStepNodeToRunTask(
                      task,
                      task?.nodeKey && stepNodesByKey instanceof Map
                        ? stepNodesByKey.get(String(task.nodeKey)) || null
                        : null
                    )}
                    template={template}
                    user={user}
                    missionActions={missionActions}
                    onHostNodeChange={onHostNodeChange}
                    onToggleBodyExpanded={
                      compact && !isActiveMissionStepStatus(task?.status)
                        ? () => handleToggleTaskBodyExpanded(task.key)
                        : null
                    }
                  />
                </NodeMissionTaskStep>
              ))}
            </div>

            {typeof handleCancelMission === 'function' ? (
              <div className="node-mission-cancel-row">
                <Button
                  backgroundColor="transparent"
                  enabled={savingMissionKey !== template.key}
                  foregroundColor={theme.foregroundMuted}
                  height={34}
                  iconKey="close"
                  label={savingMissionKey === template.key ? 'Cancelling...' : 'Cancel playbook'}
                  padding="0 12px"
                  onClick={handleCancelWorkflow}
                />
              </div>
            ) : null}
          </>
        )}
      </div>

      <Confirm
        confirmLabel="Cancel playbook"
        isOpen={cancelConfirmOpen}
        message={`Cancel ${workflowTitle}? Progress on this run will be removed, and you can choose a playbook again.`}
        secondaryLabel="Keep playbook"
        setOpen={setCancelConfirmOpen}
        title="Cancel playbook"
        onConfirm={handleConfirmCancelWorkflow}
        onSecondary={() => setCancelConfirmOpen(false)}
      />

      {typeof handleConfirmReopenTask === 'function' && typeof setReopenConfirmOpen === 'function' ? (
        <Confirm
          confirmLabel="Reopen"
          isOpen={Boolean(reopenConfirm) && reopenConfirm?.template?.key === template.key}
          message={reopenConfirm?.message || ''}
          secondaryLabel="Keep as is"
          setOpen={setReopenConfirmOpen}
          title={`Reopen ${reopenConfirm?.title || 'task'}?`}
          width={480}
          onConfirm={handleConfirmReopenTask}
          onSecondary={() => setReopenConfirmOpen(false)}
        />
      ) : null}

      {!isPlaybookFocusEmbed ? (
        <PlaybookItemFocusModal
          hostNode={hostNode}
          isOpen={focusOpen}
          missionActions={missionActions}
          missionProgress={missionProgress}
          missionRun={missionRun}
          stepNodesByKey={stepNodesByKey}
          template={template}
          user={user}
          onClose={handleClosePlaybookFocus}
          onHostNodeChange={onHostNodeChange}
        />
      ) : null}
    </div>
  );
});

NodeMission.displayName = 'NodeMission';

export default NodeMission;
